import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

/* ===========================================================================
   InteractiveGlobe — drop-in React port of the Yapp design-system globe.
   ---------------------------------------------------------------------------
   A draggable, auto-rotating 3D globe painted from world GeoJSON. Hovering a
   country reports it; clicking selects it and fires onCountrySelect with the
   country details — so you can show "this message came from <country>".

   Countries present in `messageCountries` render darker and get a pulsing
   marker.

   Props:
     messageCountries  {object}  keyed by ISO alpha-2, e.g.
                                 { JP:{count:2}, BR:{count:1} }. Anything truthy
                                 marks that country as "has a message".
     earthColor        {string}  land colour      (default '#2a932a')
     oceanColor        {string}  ocean colour     (default '#0a3a66')
     geojsonUrl        {string}  world polygons   (default Natural Earth CDN;
                                 point at your bundled /ne_110m_admin_0_countries.json)
     onCountrySelect   {func}    (country | null) => void
                                 country = { name, iso, continent, hasMessage }
     onCountryHover    {func}    (country | null) => void
     style / className          forwarded to the container div

   Requires: three (already in your package.json).
   ========================================================================= */

const TEX_W = 2048, TEX_H = 1024;

function hexToRgb(hex) {
  hex = hex.replace('#', '');
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
}
function shade(hex, pct) {
  const [r, g, b] = hexToRgb(hex);
  const f = 1 - pct / 100;
  const h = n => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r * f)}${h(g * f)}${h(b * f)}`;
}
function lighten(hex, pct) {
  const [r, g, b] = hexToRgb(hex);
  const f = pct / 100;
  const h = n => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r + (255 - r) * f)}${h(g + (255 - g) * f)}${h(b + (255 - b) * f)}`;
}
function pointInRing(lon, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
    if (((yi > lat) !== (yj > lat)) && (lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}
function featureContains(feature, lon, lat) {
  const g = feature.geometry;
  const polys = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
  for (const poly of polys) {
    if (poly.length && pointInRing(lon, lat, poly[0])) {
      let inHole = false;
      for (let h = 1; h < poly.length; h++) if (pointInRing(lon, lat, poly[h])) { inHole = true; break; }
      if (!inHole) return true;
    }
  }
  return false;
}
// Read name / iso from either the slim DS geojson or raw Natural Earth.
const propName = p => p.name || p.ADMIN || p.NAME || p.admin;
const propIso = p => (p.iso || p.ISO_A2_EH || p.ISO_A2 || '').toUpperCase();

export default function InteractiveGlobe({
  messageCountries = {},
  earthColor = '#2faf68',
  oceanColor = '#145fb0',
  geojsonUrl = '/world.geojson',
  userLocation = null,
  markerColor = 0x667eea,
  graticuleColor = 'rgba(255,255,255,0.06)',
  glowColor = 0x1e90ff,
  glowOpacity = 0.16,
  selectedColor = '#ffd54a',
  coastColor = 'rgba(10,30,30,0.5)',
  coastWidth = 0.5,
  ambient = 0.65,
  sunIntensity = 1.2,
  flat = false,
  onCountrySelect,
  onCountryHover,
  style,
  className,
}) {
  const containerRef = useRef(null);
  const stateRef = useRef({ selectedIso: null, hoverIso: null });

  useEffect(() => {
    let disposed = false;
    let cleanup = () => {};

    fetch(geojsonUrl).then(r => r.json()).then(geojson => {
      if (disposed || !containerRef.current) return;
      const container = containerRef.current;
      const st = stateRef.current;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 100);
      camera.position.z = 2.6;
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(container.clientWidth, container.clientHeight);
      renderer.setClearColor(0x000000, 0);
      container.appendChild(renderer.domElement);
      renderer.domElement.style.cursor = 'grab';

      const canvas = document.createElement('canvas');
      canvas.width = TEX_W; canvas.height = TEX_H;
      const ctx = canvas.getContext('2d');

      const paint = () => {
        ctx.fillStyle = oceanColor;
        ctx.fillRect(0, 0, TEX_W, TEX_H);
        const toXY = (lon, lat) => [((lon + 180) / 360) * TEX_W, ((90 - lat) / 180) * TEX_H];
        const drawPoly = rings => {
          const ring = rings[0];
          ctx.beginPath();
          const [sx, sy] = toXY(ring[0][0], ring[0][1]);
          ctx.moveTo(sx, sy);
          for (let i = 1; i < ring.length; i++) { const [x, y] = toXY(ring[i][0], ring[i][1]); ctx.lineTo(x, y); }
          ctx.closePath(); ctx.fill(); ctx.stroke();
        };
        for (const f of geojson.features) {
          const iso = propIso(f.properties);
          const hasMsg = !!messageCountries[iso];
          const selected = iso && iso === st.selectedIso;
          const hovered = iso && iso === st.hoverIso;
          let fill = hasMsg ? shade(earthColor, 38) : earthColor;
          if (hovered) fill = lighten(fill, 28);
          if (selected) fill = selectedColor;
          ctx.fillStyle = fill;
          ctx.strokeStyle = selected ? '#fff' : coastColor;
          ctx.lineWidth = selected ? 1.5 : coastWidth;
          const g = f.geometry;
          if (g.type === 'Polygon') drawPoly(g.coordinates);
          else g.coordinates.forEach(p => drawPoly(p));
        }
        ctx.strokeStyle = graticuleColor; ctx.lineWidth = 0.5;
        for (let lat = -60; lat <= 60; lat += 30) { const y = ((90 - lat) / 180) * TEX_H; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(TEX_W, y); ctx.stroke(); }
        for (let lon = -150; lon <= 150; lon += 30) { const x = ((lon + 180) / 360) * TEX_W; ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, TEX_H); ctx.stroke(); }
        texture.needsUpdate = true;
      };

      const texture = new THREE.CanvasTexture(canvas);
      texture.encoding = THREE.sRGBEncoding;
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
      paint();

      const globe = new THREE.Mesh(
        new THREE.SphereGeometry(1, 96, 96),
        flat
          ? new THREE.MeshBasicMaterial({ map: texture })
          : new THREE.MeshPhongMaterial({ map: texture, shininess: 6, specular: 0x113355 })
      );
      scene.add(globe);
      scene.add(new THREE.Mesh(
        new THREE.SphereGeometry(1.09, 64, 64),
        new THREE.MeshBasicMaterial({ color: glowColor, transparent: true, opacity: glowOpacity, side: THREE.BackSide })
      ));
      scene.add(new THREE.AmbientLight(0xffffff, ambient));
      const sun = new THREE.PointLight(0xffffff, sunIntensity); sun.position.set(6, 4, 6); scene.add(sun);

      // markers for message countries
      const markers = [];
      const centroid = f => {
        const g = f.geometry, polys = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
        let best = polys[0][0], len = 0;
        for (const p of polys) if (p[0].length > len) { len = p[0].length; best = p[0]; }
        let sx = 0, sy = 0; best.forEach(([x, y]) => { sx += x; sy += y; });
        return [sx / best.length, sy / best.length];
      };
      const addMarker = (lat, lon, color = 0x667eea) => {
        const phi = (90 - lat) * Math.PI / 180, theta = (lon + 180) * Math.PI / 180, r = 1.015;
        const x = -(r * Math.sin(phi) * Math.cos(theta)), z = r * Math.sin(phi) * Math.sin(theta), y = r * Math.cos(phi);
        const dot = new THREE.Mesh(new THREE.SphereGeometry(0.012, 12, 12), new THREE.MeshBasicMaterial({ color }));
        dot.position.set(x, y, z);
        const ring = new THREE.Mesh(new THREE.SphereGeometry(0.022, 12, 12), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.4 }));
        ring.position.set(x, y, z);
        globe.add(dot); globe.add(ring); markers.push(ring);
      };
      Object.keys(messageCountries).forEach(iso => {
        const f = geojson.features.find(ft => propIso(ft.properties) === iso);
        if (f) { const [lon, lat] = centroid(f); addMarker(lat, lon, markerColor); }
      });
      if (userLocation && userLocation.latitude && userLocation.longitude) {
        addMarker(userLocation.latitude, userLocation.longitude, 0xff3b3b);
      }

      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();
      let autoRotate = true, dragging = false, moved = false, last = { x: 0, y: 0 };

      const pick = (clientX, clientY) => {
        const rect = renderer.domElement.getBoundingClientRect();
        mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(mouse, camera);
        const hits = raycaster.intersectObject(globe, false);
        if (!hits.length || !hits[0].uv) return null;
        const lon = hits[0].uv.x * 360 - 180, lat = hits[0].uv.y * 180 - 90;
        for (const f of geojson.features) {
          if (featureContains(f, lon, lat)) {
            const iso = propIso(f.properties);
            return { name: propName(f.properties), iso, continent: f.properties.continent || f.properties.CONTINENT, hasMessage: !!messageCountries[iso] };
          }
        }
        return null;
      };

      const down = (x, y) => { dragging = true; moved = false; autoRotate = false; last = { x, y }; };
      const move = (x, y) => {
        if (dragging) {
          const dx = x - last.x, dy = y - last.y;
          if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
          globe.rotation.y += dx * 0.005;
          globe.rotation.x = Math.max(-1.2, Math.min(1.2, globe.rotation.x + dy * 0.005));
          last = { x, y };
        } else {
          const c = pick(x, y), iso = c ? c.iso : null;
          if (iso !== st.hoverIso) { st.hoverIso = iso; paint(); onCountryHover && onCountryHover(c); }
          renderer.domElement.style.cursor = c ? 'pointer' : 'grab';
        }
      };
      const up = () => { dragging = false; setTimeout(() => { autoRotate = true; }, 1500); };

      const onMd = e => down(e.clientX, e.clientY);
      const onMm = e => move(e.clientX, e.clientY);
      const onClick = e => {
        if (moved) return;
        const c = pick(e.clientX, e.clientY);
        st.selectedIso = c ? c.iso : null; paint();
        onCountrySelect && onCountrySelect(c);
      };
      const onWheel = e => { e.preventDefault(); camera.position.z = Math.max(1.4, Math.min(4.5, camera.position.z + (e.deltaY > 0 ? 0.18 : -0.18))); };
      const onTs = e => e.touches[0] && down(e.touches[0].clientX, e.touches[0].clientY);
      const onTm = e => e.touches[0] && move(e.touches[0].clientX, e.touches[0].clientY);
      const onResize = () => {
        const w = container.clientWidth, h = container.clientHeight;
        if (!w || !h) return;
        camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h);
      };
      renderer.domElement.addEventListener('mousedown', onMd);
      window.addEventListener('mousemove', onMm);
      window.addEventListener('mouseup', up);
      renderer.domElement.addEventListener('click', onClick);
      renderer.domElement.addEventListener('wheel', onWheel, { passive: false });
      renderer.domElement.addEventListener('touchstart', onTs, { passive: true });
      renderer.domElement.addEventListener('touchmove', onTm, { passive: true });
      renderer.domElement.addEventListener('touchend', up);
      window.addEventListener('resize', onResize);

      let raf;
      const animate = () => {
        raf = requestAnimationFrame(animate);
        if (autoRotate) globe.rotation.y += 0.0011;
        const s = 1 + Math.sin(performance.now() * 0.003) * 0.25;
        markers.forEach(m => { m.scale.set(s, s, s); m.material.opacity = 0.4 / s; });
        renderer.render(scene, camera);
      };
      animate();

      cleanup = () => {
        cancelAnimationFrame(raf);
        renderer.domElement.removeEventListener('mousedown', onMd);
        window.removeEventListener('mousemove', onMm);
        window.removeEventListener('mouseup', up);
        renderer.domElement.removeEventListener('click', onClick);
        renderer.domElement.removeEventListener('wheel', onWheel);
        renderer.domElement.removeEventListener('touchstart', onTs);
        renderer.domElement.removeEventListener('touchmove', onTm);
        renderer.domElement.removeEventListener('touchend', up);
        window.removeEventListener('resize', onResize);
        renderer.dispose();
        if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      };
    });

    return () => { disposed = true; cleanup(); };
    // re-init when colors or the message set change
  }, [earthColor, oceanColor, geojsonUrl, userLocation, markerColor, graticuleColor, glowColor, glowOpacity, selectedColor, coastColor, coastWidth, ambient, sunIntensity, flat, JSON.stringify(Object.keys(messageCountries).sort())]);

  return <div ref={containerRef} className={className} style={{ width: '100%', height: '100%', ...style }} />;
}
